package com.htmlagent.site.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.htmlagent.site.theme.FadingPageStyle
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div

/**
 * A page that arrives with a fade and leaves with one before handing over to the next route.
 *
 * The exit half is the reason this exists. A route change replaces the whole tree at once, so a
 * page cannot animate itself out on the way: by the time the new one mounts, the old one is gone
 * and all you can do is fade the arrival in. Getting both halves means the departing page has to
 * ask for the change itself — mark itself as leaving, let that paint, and navigate after — which
 * is what [content] receives as `leave`.
 *
 * The wait before navigating is skipped outright under reduced motion. The global rule collapses
 * the transition, but not a delay written in Kotlin: without this check, somebody who asked for
 * no movement would get no movement and still wait for it.
 */
@Composable
fun FadingPage(modifier: Modifier = Modifier, content: @Composable (leave: (String) -> Unit) -> Unit) {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    var leaving by remember { mutableStateOf(false) }

    val leave: (String) -> Unit = { route ->
        if (prefersReducedMotion()) {
            ctx.router.navigateTo(route)
        } else {
            leaving = true
            scope.launch {
                delay(LEAVE_MS)
                ctx.router.navigateTo(route)
            }
        }
    }

    Div(
        FadingPageStyle.toModifier().then(modifier).toAttrs {
            if (leaving) attr("data-leaving", "true")
        },
    ) {
        content(leave)
    }
}

/** How long the exit takes. It matches `Motion.PageLeave`, which is what paints it. */
private const val LEAVE_MS = 150L

/** Whether the person has asked their system for less movement. */
internal fun prefersReducedMotion(): Boolean =
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
